import React from 'react';
import { Wallet, TrendingUp, Calendar, Target, PiggyBank } from 'lucide-react';

/**
 * Quick Stats Panel - 4 key metrics displayed at the top
 * Beautiful gradient cards with icons
 */
export default function QuickStatsPanel({
  currentRetirementSavings,
  totalAnnualContributions,
  retirementAccounts,
  yearsToRetirement,
  retirementAge,
  fundingRatio,
  statePensionEstimate,
  hasResults,
  currencySymbol
}) {
  // Determine funding ratio gradient colors
  const getFundingGradient = () => {
    if (!hasResults || fundingRatio === undefined) {
      return 'from-purple-500 to-purple-600';
    }
    if (fundingRatio >= 1.0) return 'from-green-500 to-green-600';
    if (fundingRatio >= 0.75) return 'from-amber-500 to-amber-600';
    return 'from-red-500 to-red-600';
  };

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {/* Current Savings Card */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-blue-100">Current Savings</div>
            <Wallet className="h-6 w-6 text-blue-100" />
          </div>
          <div className="text-3xl font-bold mb-1">
            {currencySymbol}{currentRetirementSavings.toLocaleString()}
          </div>
          <p className="text-xs text-blue-100">
            Across {retirementAccounts.length} {retirementAccounts.length === 1 ? 'account' : 'accounts'}
          </p>
        </div>
      </div>

      {/* Annual Contributions Card */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-green-100">Annual Contributions</div>
            <TrendingUp className="h-6 w-6 text-green-100" />
          </div>
          <div className="text-3xl font-bold mb-1">
            {currencySymbol}{totalAnnualContributions.toLocaleString()}
          </div>
          <p className="text-xs text-green-100">
            {currencySymbol}{(totalAnnualContributions / 12).toLocaleString()}/month
          </p>
        </div>
      </div>

      {/* Years to Retirement Card */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 p-6 text-white shadow-lg hover:shadow-xl transition-shadow">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-orange-100">Years to Retirement</div>
            <Calendar className="h-6 w-6 text-orange-100" />
          </div>
          <div className="text-3xl font-bold mb-1">
            {yearsToRetirement}
          </div>
          <p className="text-xs text-orange-100">
            Retiring at age {retirementAge}
          </p>
        </div>
      </div>

      {/* Funding Ratio / State Pension Card */}
      <div className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${getFundingGradient()} p-6 text-white shadow-lg hover:shadow-xl transition-shadow`}>
        <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-white/90">
              {hasResults ? 'Funding Ratio' : 'State Pension'}
            </div>
            {hasResults ? (
              <Target className="h-6 w-6 text-white/90" />
            ) : (
              <PiggyBank className="h-6 w-6 text-white/90" />
            )}
          </div>
          {hasResults && fundingRatio !== undefined ? (
            <>
              <div className="text-3xl font-bold mb-1">
                {Math.round(fundingRatio * 100)}%
              </div>
              <p className="text-xs text-white/90">
                {fundingRatio >= 1.0 ? '✓ On track' : fundingRatio >= 0.75 ? '⚠ Close' : '✗ Underfunded'}
              </p>
            </>
          ) : (
            <>
              <div className="text-3xl font-bold mb-1">
                {currencySymbol}{statePensionEstimate.toLocaleString()}
              </div>
              <p className="text-xs text-white/90">
                Estimated annual
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
